import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CustomPagination from "../../../components/CustomPagination/CustomPagination";
import { BID_V2_COLORS } from "../lib/bidAutomationStyles";
import BidAutomationLogConfigDialog from "./BidAutomationLogConfigDialog";
import {
  BID_V2_CHILD_RULE_TYPE,
  BID_V2_LOG_COLUMNS,
  BID_V2_LOGS_CHILD_SKELETON_ROWS,
  BID_V2_LOGS_DEFAULT_PAGE_SIZE,
  BID_V2_LOGS_PAGE_SIZE_OPTIONS,
  BID_V2_LOGS_PARENT_SKELETON_ROWS,
  BID_V2_PARENT_RULE_TYPE,
  buildBidAutomationLogsUrl,
  buildChildLogsFiltering,
  buildParentLogsFiltering,
  mapBidLogToRow,
  parseLogsApiResponse,
  resolveLogsTotalCount,
} from "../lib/bidAutomationLogs";

const EXPANDED_ROW_BG = "rgba(97, 72, 246, 0.08)";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "96%",
  maxWidth: 1280,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerCellSx = {
  fontWeight: 700,
  fontSize: 11,
  color: "text.secondary",
  bgcolor: "grey.100",
  py: 1.25,
  whiteSpace: "nowrap",
};

function getParentRowKey(row) {
  return `${row.trackId}::${row.timestamp}::${row.event}`;
}

function LogCell({ value }) {
  const text = value == null || value === "" ? "—" : value;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {text}
    </Typography>
  );
}

function CenteredMessage({ children, minHeight = 120 }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
      minHeight={minHeight}
      py={3}
    >
      {children}
    </Box>
  );
}

function LogSkeletonRows({ count, isChild = false }) {
  return Array.from({ length: count }, (_, rowIndex) => (
    <TableRow
      key={`log-skeleton-${isChild ? "child" : "parent"}-${rowIndex}`}
      sx={isChild ? { bgcolor: EXPANDED_ROW_BG } : undefined}
    >
      <TableCell sx={{ width: 48, py: 1 }}>
        {!isChild ? <Skeleton variant="circular" width={24} height={24} /> : null}
      </TableCell>
      {BID_V2_LOG_COLUMNS.map((col, colIndex) => (
        <TableCell key={col.key} sx={{ minWidth: col.minWidth, py: 1 }}>
          <Skeleton
            variant="text"
            width={colIndex % 3 === 0 ? "92%" : colIndex % 3 === 1 ? "72%" : "58%"}
            height={22}
          />
        </TableCell>
      ))}
    </TableRow>
  ));
}

function LogDataRow({ row, isChild = false }) {
  return (
    <TableRow sx={isChild ? { bgcolor: EXPANDED_ROW_BG } : undefined} hover={!isChild}>
      <TableCell sx={{ width: 48 }} />
      {BID_V2_LOG_COLUMNS.map((col) => (
        <TableCell key={col.key} sx={{ minWidth: col.minWidth, verticalAlign: "top" }}>
          <LogCell value={row[col.key]} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function LogsPaginationBar({
  page,
  pageLimit,
  totalCount,
  loading,
  onPageChange,
  onPageLimitChange,
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));
  if (loading || (totalCount <= 0 && page <= 1)) return null;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      py={1.5}
      px={1}
      onClick={(event) => event.stopPropagation()}
    >
      <Box display="flex" alignItems="center" gap={1} color="#4f5366">
        <Typography variant="body2" sx={{ fontSize: "12px" }}>
          Page Size
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={pageLimit}
            onChange={(event) => onPageLimitChange(Number(event.target.value))}
            disabled={loading}
            sx={{ height: 36, fontSize: "12px", color: "#333333" }}
            MenuProps={{ disableScrollLock: true }}
          >
            {BID_V2_LOGS_PAGE_SIZE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomPagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        lazyLoading={loading}
        totalItems={totalCount}
        itemsPerPage={pageLimit}
        hidePagination={!totalCount}
      />
    </Box>
  );
}

function ExpandedChildSection({
  childRows,
  childLoading,
  childError,
  page,
  pageLimit,
  totalCount,
  onPageChange,
  onPageLimitChange,
}) {
  const colSpan = BID_V2_LOG_COLUMNS.length + 1;

  if (childLoading) {
    return <LogSkeletonRows count={BID_V2_LOGS_CHILD_SKELETON_ROWS} isChild />;
  }

  if (childError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Typography variant="body2" color="error" py={1.5} px={2}>
            {childError}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (!childRows.length) {
    return (
      <>
        <TableRow>
          <TableCell colSpan={colSpan}>
            <CenteredMessage minHeight={80}>
              <Typography variant="body2" color="text.secondary">
                No targeting-level logs found
              </Typography>
            </CenteredMessage>
          </TableCell>
        </TableRow>
        {page > 1 ? (
          <TableRow>
            <TableCell colSpan={colSpan} sx={{ py: 0 }}>
              <LogsPaginationBar
                page={page}
                pageLimit={pageLimit}
                totalCount={totalCount}
                loading={false}
                onPageChange={onPageChange}
                onPageLimitChange={onPageLimitChange}
              />
            </TableCell>
          </TableRow>
        ) : null}
      </>
    );
  }

  return (
    <>
      {childRows.map((childRow) => (
        <LogDataRow key={childRow.rowKey} row={childRow} isChild />
      ))}
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0 }}>
          <LogsPaginationBar
            page={page}
            pageLimit={pageLimit}
            totalCount={totalCount}
            loading={childLoading}
            onPageChange={onPageChange}
            onPageLimitChange={onPageLimitChange}
          />
        </TableCell>
      </TableRow>
    </>
  );
}

export default function BidAutomationLogsModal({ open, onClose, ruleName, clientId, source }) {
  const [expanded, setExpanded] = useState(new Set());
  const [parentRows, setParentRows] = useState([]);
  const [parentLoading, setParentLoading] = useState(false);
  const [parentError, setParentError] = useState("");
  const [childRowsMap, setChildRowsMap] = useState({});
  const [childLoadingMap, setChildLoadingMap] = useState({});
  const [childErrorMap, setChildErrorMap] = useState({});
  const [parentPage, setParentPage] = useState(1);
  const [parentPageLimit, setParentPageLimit] = useState(BID_V2_LOGS_DEFAULT_PAGE_SIZE);
  const [parentTotalCount, setParentTotalCount] = useState(0);
  const [childPageMap, setChildPageMap] = useState({});
  const [childPageLimitMap, setChildPageLimitMap] = useState({});
  const [childTotalCountMap, setChildTotalCountMap] = useState({});
  const [configSnapshot, setConfigSnapshot] = useState(null);

  const openLogConfig = (event, row) => {
    event.stopPropagation();
    const snapshot = row?.ruleConfig;
    if (!snapshot || typeof snapshot !== "object" || !Object.keys(snapshot).length) {
      toast.warning("This log does not include a rule config");
      return;
    }
    setConfigSnapshot(snapshot);
  };

  const resetState = useCallback(() => {
    setExpanded(new Set());
    setParentRows([]);
    setParentLoading(false);
    setParentError("");
    setChildRowsMap({});
    setChildLoadingMap({});
    setChildErrorMap({});
    setParentPage(1);
    setParentPageLimit(BID_V2_LOGS_DEFAULT_PAGE_SIZE);
    setParentTotalCount(0);
    setChildPageMap({});
    setChildPageLimitMap({});
    setChildTotalCountMap({});
    setConfigSnapshot(null);
  }, []);

  const fetchParentLogs = useCallback(async () => {
    if (!clientId || !source) return;
    setParentLoading(true);
    setParentError("");
    setExpanded(new Set());
    try {
      const zeroBasedPage = parentPage - 1;
      const url = buildBidAutomationLogsUrl(clientId, {
        source,
        filtering: buildParentLogsFiltering(ruleName),
        ruleType: BID_V2_PARENT_RULE_TYPE,
        page: zeroBasedPage,
        limit: parentPageLimit,
      });
      const { data } = await axios.get(url);
      const { list, totalCount } = parseLogsApiResponse(data);
      setParentRows(list.map((log) => mapBidLogToRow(log)));
      setParentTotalCount(
        resolveLogsTotalCount(totalCount, list.length, zeroBasedPage, parentPageLimit)
      );
    } catch (error) {
      setParentError(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Failed to fetch logs"
      );
      setParentRows([]);
      setParentTotalCount(0);
    } finally {
      setParentLoading(false);
    }
  }, [clientId, source, ruleName, parentPage, parentPageLimit]);

  const fetchChildLogs = useCallback(
    async (parentRow, page = 1, pageLimit = BID_V2_LOGS_DEFAULT_PAGE_SIZE) => {
      const parentKey = getParentRowKey(parentRow);
      const trackId = parentRow.trackId;
      if (!clientId || !source || !trackId) {
        setChildErrorMap((prev) => ({
          ...prev,
          [parentKey]: "Missing track id for targeting logs",
        }));
        return;
      }
      setChildLoadingMap((prev) => ({ ...prev, [parentKey]: true }));
      setChildErrorMap((prev) => ({ ...prev, [parentKey]: "" }));
      const zeroBasedPage = page - 1;
      try {
        const url = buildBidAutomationLogsUrl(clientId, {
          source,
          filtering: buildChildLogsFiltering(trackId, ruleName),
          ruleType: BID_V2_CHILD_RULE_TYPE,
          page: zeroBasedPage,
          limit: pageLimit,
        });
        const { data } = await axios.get(url);
        const { list, totalCount } = parseLogsApiResponse(data);
        setChildRowsMap((prev) => ({
          ...prev,
          [parentKey]: list.map((log) => mapBidLogToRow(log, { isChild: true })),
        }));
        setChildTotalCountMap((prev) => ({
          ...prev,
          [parentKey]: resolveLogsTotalCount(totalCount, list.length, zeroBasedPage, pageLimit),
        }));
      } catch (error) {
        setChildErrorMap((prev) => ({
          ...prev,
          [parentKey]:
            error?.response?.data?.message ??
            error?.response?.data?.error ??
            error?.message ??
            "Failed to fetch targeting logs",
        }));
      } finally {
        setChildLoadingMap((prev) => ({ ...prev, [parentKey]: false }));
      }
    },
    [clientId, source, ruleName]
  );

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !clientId || !source) return;
    fetchParentLogs();
  }, [open, clientId, source, fetchParentLogs]);

  const handleToggleRow = useCallback(
    (row) => {
      const parentKey = getParentRowKey(row);
      setExpanded((prev) => {
        const next = new Set(prev);
        const isOpening = !next.has(parentKey);
        if (next.has(parentKey)) next.delete(parentKey);
        else next.add(parentKey);
        if (isOpening) {
          const page = childPageMap[parentKey] ?? 1;
          const pageLimit = childPageLimitMap[parentKey] ?? BID_V2_LOGS_DEFAULT_PAGE_SIZE;
          fetchChildLogs(row, page, pageLimit);
        }
        return next;
      });
    },
    [childPageLimitMap, childPageMap, fetchChildLogs]
  );

  const handleParentPageLimitChange = (nextLimit) => {
    setParentPageLimit(nextLimit);
    setParentPage(1);
  };

  const handleChildPageChange = (row, nextPage) => {
    const parentKey = getParentRowKey(row);
    const pageLimit = childPageLimitMap[parentKey] ?? BID_V2_LOGS_DEFAULT_PAGE_SIZE;
    setChildPageMap((prev) => ({ ...prev, [parentKey]: nextPage }));
    fetchChildLogs(row, nextPage, pageLimit);
  };

  const handleChildPageLimitChange = (row, nextLimit) => {
    const parentKey = getParentRowKey(row);
    setChildPageLimitMap((prev) => ({ ...prev, [parentKey]: nextLimit }));
    setChildPageMap((prev) => ({ ...prev, [parentKey]: 1 }));
    fetchChildLogs(row, 1, nextLimit);
  };

  const colSpan = BID_V2_LOG_COLUMNS.length + 1;

  return (
    <>
    <Modal open={open} onClose={onClose} disableEnforceFocus={Boolean(configSnapshot)}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          px={2.5}
          py={2}
          borderBottom={1}
          borderColor="divider"
          flexShrink={0}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: BID_V2_COLORS.title }}>
              Bid Automation Logs
            </Typography>
            {ruleName ? (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Rule Name: <strong>{ruleName}</strong>
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Expand a row to view targeting-level log details.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close logs" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box flex={1} overflow="hidden" p={1.25}>
          <TableContainer
            sx={{
              maxHeight: "calc(90vh - 220px)",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 48 }} />
                  {BID_V2_LOG_COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ ...headerCellSx, minWidth: col.minWidth }}>
                      {col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parentLoading ? (
                  <LogSkeletonRows count={BID_V2_LOGS_PARENT_SKELETON_ROWS} />
                ) : parentError ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <CenteredMessage>
                        <Typography variant="body2" color="error">
                          {parentError}
                        </Typography>
                      </CenteredMessage>
                    </TableCell>
                  </TableRow>
                ) : !parentRows.length ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <CenteredMessage>
                        <Typography variant="body2" color="text.secondary">
                          No logs found
                        </Typography>
                      </CenteredMessage>
                    </TableCell>
                  </TableRow>
                ) : (
                  parentRows.map((row) => {
                    const parentKey = getParentRowKey(row);
                    const isOpen = expanded.has(parentKey);
                    return (
                      <Fragment key={parentKey}>
                        <TableRow hover sx={{ cursor: "pointer" }} onClick={() => handleToggleRow(row)}>
                          <TableCell sx={{ width: 48, py: 0.5 }}>
                            <IconButton
                              size="small"
                              aria-label={isOpen ? "Collapse row" : "Expand row"}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleRow(row);
                              }}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          {BID_V2_LOG_COLUMNS.map((col) => (
                            <TableCell key={col.key} sx={{ minWidth: col.minWidth, verticalAlign: "top" }}>
                              {col.key === "triggeredBy" ? (
                                <Box>
                                  <LogCell value={row.triggeredBy} />
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={(event) => openLogConfig(event, row)}
                                    sx={{
                                      mt: 0.25,
                                      px: 0,
                                      minWidth: 0,
                                      fontWeight: 600,
                                      textTransform: "none",
                                    }}
                                  >
                                    View Rule Details
                                  </Button>
                                </Box>
                              ) : (
                                <LogCell value={row[col.key]} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        {isOpen ? (
                          <ExpandedChildSection
                            childRows={childRowsMap[parentKey] || []}
                            childLoading={childLoadingMap[parentKey]}
                            childError={childErrorMap[parentKey]}
                            page={childPageMap[parentKey] ?? 1}
                            pageLimit={childPageLimitMap[parentKey] ?? BID_V2_LOGS_DEFAULT_PAGE_SIZE}
                            totalCount={childTotalCountMap[parentKey] ?? 0}
                            onPageChange={(nextPage) => handleChildPageChange(row, nextPage)}
                            onPageLimitChange={(nextLimit) => handleChildPageLimitChange(row, nextLimit)}
                          />
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <LogsPaginationBar
            page={parentPage}
            pageLimit={parentPageLimit}
            totalCount={parentTotalCount}
            loading={parentLoading}
            onPageChange={setParentPage}
            onPageLimitChange={handleParentPageLimitChange}
          />
        </Box>
      </Box>
    </Modal>
      <BidAutomationLogConfigDialog
        open={Boolean(configSnapshot)}
        snapshot={configSnapshot}
        source={source}
        onClose={() => setConfigSnapshot(null)}
      />
    </>
  );
}
